/**
 * Contrôle pesé d'une livraison B2B, le lundi avant de partir (02/11/2026).
 *
 * Chaque pesée = ce qui est sur la balance à colonne (une caisse, un sac…) :
 * une ou plusieurs photos (contenu, afficheur) et le poids lu. On pèse en autant
 * de fois qu'il faut. Le total, tare des caisses déduite, se compare au poids de
 * la commande. Chaque pesée est enregistrée aussitôt : un téléphone qui se
 * recharge ne perd rien. Le verdict vient du serveur (`controle-pesee.ts`).
 *
 * Pensée pour l'iPhone (03/11/2026) : plein écran, zones sûres (encoche, barre
 * d'accueil), photos réduites avant l'envoi (4G devant la camionnette).
 *
 * Photo : appareil photo NATIF (`<input capture>`) — dans une PWA iOS installée,
 * l'autorisation de `getUserMedia` recharge l'app (cf. ProductCountModal).
 */
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import api from '../../services/api';
import { useTenant } from '../../contexts/TenantContext';
import { prepareImageForUpload } from '../../utils/imagePrep';

interface TypeCaisse { nom: string; poids_kg: number }
interface Pesee { poids_kg: number; type?: string | null; photos?: string[]; photo_url?: string | null }
interface Verdict {
  brut_kg: number; tare_kg: number; net_kg: number; attendu_kg: number; ecart_kg: number; tolerance_kg: number;
  statut: 'incomplet' | 'ok' | 'trop_leger' | 'trop_lourd' | 'tare_inconnue';
}
interface Etat {
  commande: { id: string; numero: string; client: string };
  lignes: Array<{ nom: string; quantite: number; unite: string; poids_kg: number | null }>;
  attendu_kg: number;
  inconnues: string[];
  types: TypeCaisse[];
  tolerance_pct: number;
  controle: { pesees: Pesee[]; valide: boolean; motif: string | null; par: string | null; le: string } | null;
  verdict: Verdict;
}

/** Pesée en cours de saisie : le poids est du texte tant qu'on tape (virgule acceptée). */
interface Saisie { poids: string; type: string | null; photos: string[] }

/** Même nom que `SANS_CAISSE` côté serveur : rien à retirer du poids lu. */
const SANS_CAISSE = 'Sans caisse';
/** Comme `PHOTOS_PAR_PESEE_MAX` côté serveur. */
const PHOTOS_MAX = 4;

/** Caisses du magasin, pesées vides le 02/11/2026 (photos de Philippe) : proposées au premier réglage. */
const CAISSES_PROPOSEES = [
  { nom: 'EPS 246 / 24603', poids: '2,000' },
  { nom: 'EPS 186', poids: '1,545' },
  { nom: 'EPS 154', poids: '0,695' },
];

const kg = (n: number) => `${n.toLocaleString('fr-BE', { minimumFractionDigits: 1, maximumFractionDigits: 2 })} kg`;
const nombre = (s: string) => Number(s.replace(',', '.'));

const STATUTS: Record<Verdict['statut'], { titre: string; classe: string }> = {
  incomplet: { titre: 'Pesez les caisses', classe: 'bg-gray-50 border-gray-300 text-gray-800' },
  ok: { titre: '✓ Poids conforme à la commande', classe: 'bg-emerald-50 border-emerald-400 text-emerald-900' },
  trop_leger: { titre: '✕ Trop léger : il manque quelque chose', classe: 'bg-red-50 border-red-400 text-red-900' },
  trop_lourd: { titre: '⚠ Plus lourd que prévu : caisse d’une autre crèche ?', classe: 'bg-amber-50 border-amber-400 text-amber-900' },
  tare_inconnue: { titre: '⚠ Poids d’une caisse vide à régler', classe: 'bg-amber-50 border-amber-400 text-amber-900' },
};

export default function ControlePeseeModal({ orderId, onClose }: { orderId: string; onClose: (change: boolean) => void }) {
  const { tenant, reloadTenant } = useTenant();
  const [etat, setEtat] = useState<Etat | null>(null);
  const [saisies, setSaisies] = useState<Saisie[]>([]);
  const [verdict, setVerdict] = useState<Verdict | null>(null);
  const [motif, setMotif] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const [photoPour, setPhotoPour] = useState<number | null>(null);
  const [agrandie, setAgrandie] = useState<{ pesee: number; photo: number } | null>(null);
  const [voirLignes, setVoirLignes] = useState(false);
  const [reglage, setReglage] = useState<{ nom: string; poids: string }[] | null>(null);
  const [change, setChange] = useState(false);
  const fichier = useRef<HTMLInputElement>(null);

  const charger = async () => {
    try {
      const e: Etat = await api.get(`/orders/bons-enfact/${orderId}/controle-pesee`);
      setEtat(e);
      setVerdict(e.verdict);
      setMotif(e.controle?.motif ?? '');
      setSaisies((e.controle?.pesees ?? []).map((p) => ({
        poids: String(p.poids_kg).replace('.', ','),
        type: p.type ?? null,
        photos: p.photos?.length ? p.photos : p.photo_url ? [p.photo_url] : [],
      })));
      if (e.types.length === 0) setReglage(CAISSES_PROPOSEES.map((c) => ({ ...c })));
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Contrôle illisible');
      onClose(false);
    }
  };
  useEffect(() => {
    void charger();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  /** Enregistre (brouillon ou validation) ; le serveur renvoie le verdict. */
  const enregistrer = async (liste: Saisie[], valide = false) => {
    const pesees = liste
      .filter((s) => nombre(s.poids) > 0)
      .map((s) => ({ poids_kg: nombre(s.poids), type: s.type, photos: s.photos, photo_url: s.photos[0] ?? null }));
    setEnvoi(true);
    try {
      const r: any = await api.put(`/orders/bons-enfact/${orderId}/controle-pesee`, { pesees, valide, motif });
      setVerdict(r.verdict);
      setChange(true);
      if (valide) {
        toast.success('Contrôle validé');
        onClose(true);
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Contrôle non enregistré');
    } finally {
      setEnvoi(false);
    }
  };

  const modifier = (i: number, patch: (s: Saisie) => Partial<Saisie>, sauver = false) => {
    setSaisies((l) => {
      const n = l.map((s, k) => (k === i ? { ...s, ...patch(s) } : s));
      if (sauver) void enregistrer(n);
      return n;
    });
  };

  const prendrePhoto = (i: number) => {
    setPhotoPour(i);
    fichier.current?.click();
  };

  const surPhoto = async (f: File | undefined) => {
    if (!f || photoPour === null) return;
    const i = photoPour;
    setPhotoPour(null);
    setEnvoi(true);
    try {
      // Réduite à 1600 px : une photo d'iPhone fait 3 à 5 Mo, l'envoi en 4G traînait.
      const fd = new FormData();
      fd.append('file', await prepareImageForUpload(f, 1600, 0.82, 0));
      const r: any = await api.post('/uploads/image', fd);
      modifier(i, (s) => ({ photos: [...s.photos, r.url].slice(0, PHOTOS_MAX) }), true);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Photo non envoyée');
    } finally {
      setEnvoi(false);
      if (fichier.current) fichier.current.value = '';
    }
  };

  const retirerPhoto = (i: number, k: number) => {
    setAgrandie(null);
    modifier(i, (s) => ({ photos: s.photos.filter((_, j) => j !== k) }), true);
  };

  const nouvellePesee = () => {
    const i = saisies.length;
    // Même contenant que la pesée précédente : on enchaîne souvent les caisses du même type.
    const type = saisies[i - 1]?.type ?? etat?.types[0]?.nom ?? null;
    setSaisies((l) => [...l, { poids: '', type, photos: [] }]);
    prendrePhoto(i);
  };

  const retirer = (i: number) => {
    if (!window.confirm(`Retirer la pesée ${i + 1} et ses photos ?`)) return;
    setSaisies((l) => {
      const n = l.filter((_, k) => k !== i);
      void enregistrer(n);
      return n;
    });
  };

  const enregistrerReglage = async () => {
    if (!tenant?.id || !reglage) return;
    const remplies = reglage.filter((c) => c.nom.trim());
    if (remplies.length === 0 || remplies.some((c) => !c.poids.trim() || !(nombre(c.poids) >= 0 && nombre(c.poids) < 20))) {
      toast.error('Indiquez le poids de chaque caisse vide, en kg');
      return;
    }
    const caisses = remplies.map((c) => ({ nom: c.nom.trim().slice(0, 40), poids_kg: nombre(c.poids) }));
    try {
      await api.updateTenantConfig(tenant.id, {
        ecommerce: { controlePesee: { ...((tenant.config?.ecommerce as any)?.controlePesee ?? {}), caisses } },
      } as any);
      await reloadTenant();
      setReglage(null);
      await charger();
      toast.success('Poids des caisses enregistré');
    } catch {
      toast.error('Réglage non enregistré');
    }
  };

  if (!etat || !verdict) {
    return (
      <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center">
        <div className="bg-white rounded-2xl p-8 text-lg">Chargement…</div>
      </div>
    );
  }

  const s = STATUTS[verdict.statut];
  const hors = verdict.statut === 'trop_leger' || verdict.statut === 'trop_lourd';
  const photosManquantes = saisies.filter((x) => nombre(x.poids) > 0 && x.photos.length === 0).length;
  const peutValider = saisies.some((x) => nombre(x.poids) > 0) && photosManquantes === 0
    && verdict.statut !== 'tare_inconnue' && verdict.statut !== 'incomplet' && (!hors || motif.trim().length > 0);
  const contenants = [...etat.types.map((t) => ({ nom: t.nom, libelle: `${t.nom} (${kg(t.poids_kg)})` })), { nom: SANS_CAISSE, libelle: 'Sans caisse (sac, carton…)' }];
  const photoAgrandie = agrandie ? saisies[agrandie.pesee]?.photos[agrandie.photo] : null;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-stretch sm:items-center justify-center sm:p-4">
      <div className="bg-white w-full h-[100dvh] sm:h-auto sm:max-w-2xl sm:rounded-2xl flex flex-col sm:max-h-[92vh]">
        <header className="px-4 sm:px-5 pb-3 pt-[max(env(safe-area-inset-top),0.75rem)] sm:pt-4 border-b flex items-start gap-3">
          <div className="flex-1 min-w-0">
            <p className="text-sm text-gray-500 truncate">Contrôle pesé · {etat.commande.numero}</p>
            <h2 className="text-lg sm:text-xl font-bold text-gray-900 leading-tight">{etat.commande.client}</h2>
          </div>
          <button type="button" onClick={() => onClose(change)} className="shrink-0 min-h-12 px-4 rounded-xl border border-gray-300 text-base font-semibold">
            Fermer
          </button>
        </header>

        <div className="flex-1 overflow-y-auto overscroll-contain px-4 sm:px-5 py-4 space-y-4">
          <section className="rounded-xl bg-gray-50 p-4">
            <p className="text-base text-gray-700">
              Poids de la commande : <strong className="text-gray-900">{kg(etat.attendu_kg)}</strong>
              <span className="text-gray-500"> (± {kg(verdict.tolerance_kg)})</span>
            </p>
            {etat.inconnues.length > 0 && (
              <p className="mt-1 text-sm text-amber-800">
                Sans poids connu, non comptés : {etat.inconnues.join(', ')}. Seul un manque est jugé.
              </p>
            )}
            <button type="button" onClick={() => setVoirLignes((v) => !v)} className="mt-1 min-h-11 text-base font-semibold text-sky-700 underline">
              {voirLignes ? 'Masquer' : 'Voir'} les articles du bon ({etat.lignes.length})
            </button>
            {voirLignes && (
              <ul className="mt-1 text-sm divide-y">
                {etat.lignes.map((l, i) => (
                  <li key={i} className="py-1.5 flex justify-between gap-3">
                    <span className="min-w-0">{l.nom} × {l.quantite.toLocaleString('fr-BE')}</span>
                    <span className="shrink-0 tabular-nums text-gray-600">{l.poids_kg === null ? '—' : kg(l.poids_kg)}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {reglage ? (
            <section className="rounded-xl border-2 border-amber-400 p-4 space-y-3" aria-label="Poids des caisses vides">
              <p className="font-semibold text-gray-900">Poids d’une caisse VIDE (tare)</p>
              {reglage.map((c, i) => (
                <div key={i} className="flex gap-2">
                  <input
                    value={c.nom}
                    onChange={(e) => setReglage((r) => r!.map((x, k) => (k === i ? { ...x, nom: e.target.value } : x)))}
                    aria-label="Nom de la caisse"
                    className="flex-1 min-w-0 min-h-12 rounded-lg border px-3 text-base"
                  />
                  <input
                    value={c.poids}
                    inputMode="decimal"
                    onChange={(e) => setReglage((r) => r!.map((x, k) => (k === i ? { ...x, poids: e.target.value } : x)))}
                    aria-label="Poids de la caisse vide en kg"
                    placeholder="kg"
                    className="w-24 min-h-12 rounded-lg border px-3 text-base tabular-nums"
                  />
                </div>
              ))}
              <div className="flex flex-wrap gap-2">
                <button type="button" onClick={() => setReglage((r) => [...(r ?? []), { nom: '', poids: '' }])} className="min-h-12 px-4 rounded-lg border text-base">
                  + Autre caisse
                </button>
                <button type="button" onClick={enregistrerReglage} className="min-h-12 px-4 rounded-lg bg-emerald-700 text-white text-base font-semibold">
                  Enregistrer
                </button>
              </div>
            </section>
          ) : (
            <p className="text-sm text-gray-600">
              Caisse vide : {etat.types.map((t) => `${t.nom} ${kg(t.poids_kg)}`).join(' · ')}{' '}
              <button
                type="button"
                onClick={() => setReglage(etat.types.map((t) => ({ nom: t.nom, poids: String(t.poids_kg).replace('.', ',') })))}
                className="min-h-11 text-sky-700 underline"
              >
                modifier
              </button>
            </p>
          )}

          <section className="space-y-3" aria-label="Pesées">
            <p className="text-sm text-gray-600">
              Pesez en autant de fois qu’il faut : une pesée = ce qui est sur la balance. Plusieurs photos par pesée
              (contenu, afficheur), {PHOTOS_MAX} au plus.
            </p>
            {saisies.map((x, i) => (
              <div key={i} className="rounded-xl border p-3 space-y-3">
                <div className="flex items-center gap-2">
                  <p className="flex-1 font-semibold text-gray-900">Pesée {i + 1}</p>
                  <button
                    type="button"
                    onClick={() => retirer(i)}
                    className="shrink-0 min-h-11 px-3 rounded-lg text-red-700 border border-red-200 text-sm"
                    aria-label={`Retirer la pesée ${i + 1}`}
                  >
                    Retirer
                  </button>
                </div>

                <div className="flex flex-wrap gap-2" aria-label={`Photos de la pesée ${i + 1}`}>
                  {x.photos.map((url, k) => (
                    <button
                      key={url + k}
                      type="button"
                      onClick={() => setAgrandie({ pesee: i, photo: k })}
                      className="w-20 h-20 rounded-lg overflow-hidden border border-gray-200"
                      aria-label={`Voir la photo ${k + 1} de la pesée ${i + 1}`}
                    >
                      <img src={url} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                  {x.photos.length < PHOTOS_MAX && (
                    <button
                      type="button"
                      onClick={() => prendrePhoto(i)}
                      disabled={envoi}
                      className={`w-20 h-20 rounded-lg border-2 border-dashed flex flex-col items-center justify-center text-sm font-semibold ${x.photos.length === 0 ? 'border-red-400 bg-red-50 text-red-800' : 'border-gray-300 text-gray-700'}`}
                      aria-label={`Ajouter une photo à la pesée ${i + 1}`}
                    >
                      <span className="text-2xl leading-none">📷</span>
                      {x.photos.length === 0 ? 'Photo' : '+ photo'}
                    </button>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  <input
                    value={x.poids}
                    inputMode="decimal"
                    enterKeyHint="done"
                    onChange={(e) => modifier(i, () => ({ poids: e.target.value }))}
                    onBlur={() => void enregistrer(saisies)}
                    placeholder="poids lu"
                    aria-label={`Poids de la pesée ${i + 1} en kg`}
                    className="flex-1 min-w-0 min-h-14 rounded-lg border px-3 text-2xl font-semibold tabular-nums"
                  />
                  <span className="text-lg text-gray-600">kg</span>
                </div>
                <select
                  value={x.type ?? etat.types[0]?.nom ?? SANS_CAISSE}
                  onChange={(e) => modifier(i, () => ({ type: e.target.value }), true)}
                  aria-label={`Contenant de la pesée ${i + 1}`}
                  className="w-full min-h-12 rounded-lg border px-3 text-base bg-white"
                >
                  {contenants.map((c) => <option key={c.nom} value={c.nom}>{c.libelle}</option>)}
                </select>
              </div>
            ))}
            <button
              type="button"
              onClick={nouvellePesee}
              disabled={envoi}
              className="w-full min-h-14 rounded-xl border-2 border-dashed border-gray-400 text-lg font-semibold text-gray-800"
            >
              📷 Nouvelle pesée
            </button>
            <input ref={fichier} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => void surPhoto(e.target.files?.[0])} />
          </section>

          <section className={`rounded-xl border-2 p-4 ${s.classe}`} aria-live="polite">
            <p className="text-lg font-bold">{s.titre}</p>
            {verdict.statut !== 'incomplet' && (
              <p className="mt-1 text-base tabular-nums">
                {kg(verdict.brut_kg)} pesés − {kg(verdict.tare_kg)} de caisses = <strong>{kg(verdict.net_kg)}</strong> pour {kg(verdict.attendu_kg)} commandés
                {' '}({verdict.ecart_kg >= 0 ? '+' : '−'}{kg(Math.abs(verdict.ecart_kg))})
              </p>
            )}
            {photosManquantes > 0 && <p className="mt-1 text-sm font-semibold">Photo manquante sur {photosManquantes} pesée(s).</p>}
            {etat.controle?.valide && <p className="mt-1 text-sm">Validé par {etat.controle.par ?? '—'} le {new Date(etat.controle.le).toLocaleString('fr-BE')}</p>}
          </section>

          {hors && (
            <label className="block space-y-1">
              <span className="font-semibold text-gray-900">Ce qui se passe (obligatoire pour valider)</span>
              <textarea
                value={motif}
                onChange={(e) => setMotif(e.target.value)}
                rows={2}
                maxLength={300}
                placeholder="Ex. patates douces ajoutées, repesé : OK — ou : rupture, crèche prévenue"
                className="w-full rounded-lg border px-3 py-2 text-base"
              />
            </label>
          )}
        </div>

        <footer className="px-4 sm:px-5 pt-3 pb-[max(env(safe-area-inset-bottom),0.75rem)] sm:pb-4 border-t">
          <button
            type="button"
            disabled={!peutValider || envoi}
            onClick={() => void enregistrer(saisies, true)}
            className="w-full min-h-14 rounded-xl bg-emerald-700 text-white text-lg font-bold disabled:opacity-40"
          >
            {envoi ? 'Envoi…' : 'Valider le contrôle'}
          </button>
        </footer>
      </div>

      {agrandie && photoAgrandie && (
        <div className="fixed inset-0 z-[60] bg-black flex flex-col" role="dialog" aria-label="Photo de la pesée">
          <img src={photoAgrandie} alt="" className="flex-1 min-h-0 w-full object-contain" />
          <div className="flex gap-2 p-3 pb-[max(env(safe-area-inset-bottom),0.75rem)]">
            <button type="button" onClick={() => retirerPhoto(agrandie.pesee, agrandie.photo)} className="flex-1 min-h-14 rounded-xl bg-red-700 text-white text-base font-semibold">
              Retirer cette photo
            </button>
            <button type="button" onClick={() => setAgrandie(null)} className="flex-1 min-h-14 rounded-xl bg-white text-gray-900 text-base font-semibold">
              Fermer
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
